export interface Ingredient {
  nom: string;
  quantite: number;
  unite: string;
  glucides: number;
}

export interface Plat {
  id: number;
  nom: string;
  categorie: 'poisson' | 'viande' | 'volaille' | 'vegetarien';
  image: string;
  resume: string;
  nutriScore: 'A' | 'B' | 'C' | 'D' | 'E';
  glucidesTotaux: number;
  calories: number;
  ingredients: Ingredient[];
  etapes: string[];
  conseil?: string;
}

export const plats: Plat[] = [
  {
    id: 1,
    nom: 'Saumon grillé aux légumes vapeur',
    categorie: 'poisson',
    image: 'https://readdy.ai/api/search-image?query=grilled%20salmon%20fillet%20with%20steamed%20green%20vegetables%20broccoli%20zucchini%20on%20white%20plate%20healthy%20meal%20bright%20natural%20light%20food%20photography%20clean%20background&width=600&height=400&seq=plat1&orientation=landscape',
    resume: 'Filet de saumon grillé accompagné de brocolis, courgettes et carottes vapeur. Riche en oméga-3 et pauvre en glucides.',
    nutriScore: 'A',
    glucidesTotaux: 18,
    calories: 320,
    ingredients: [
      { nom: 'Filet de saumon', quantite: 150, unite: 'g', glucides: 0 },
      { nom: 'Brocolis', quantite: 120, unite: 'g', glucides: 5 },
      { nom: 'Courgette', quantite: 100, unite: 'g', glucides: 3 },
      { nom: 'Carottes', quantite: 80, unite: 'g', glucides: 7 },
      { nom: 'Huile d\'olive', quantite: 10, unite: 'ml', glucides: 0 },
      { nom: 'Citron', quantite: 30, unite: 'g', glucides: 3 },
    ],
    etapes: [
      'Préchauffer le four à 200°C ou préparer le grill.',
      'Assaisonner le saumon avec sel, poivre et jus de citron.',
      'Faire griller le saumon 12-15 minutes selon l\'épaisseur.',
      'Pendant ce temps, cuire les légumes à la vapeur 8-10 minutes.',
      'Dresser le saumon sur les légumes et arroser d\'un filet d\'huile d\'olive.',
    ],
    conseil: 'Excellent choix ! Le saumon est riche en oméga-3 et les légumes apportent des fibres qui ralentissent l\'absorption des glucides.',
  },
  {
    id: 2,
    nom: 'Bœuf sauté aux champignons et quinoa',
    categorie: 'viande',
    image: 'https://readdy.ai/api/search-image?query=sauteed%20beef%20strips%20with%20mushrooms%20and%20quinoa%20bowl%20healthy%20protein%20meal%20white%20plate%20restaurant%20food%20photography%20clean%20minimal%20background&width=600&height=400&seq=plat2&orientation=landscape',
    resume: 'Émincé de bœuf maigre sauté avec champignons de Paris, accompagné de quinoa complet. Repas équilibré et rassasiant.',
    nutriScore: 'B',
    glucidesTotaux: 42,
    calories: 480,
    ingredients: [
      { nom: 'Bœuf maigre émincé', quantite: 150, unite: 'g', glucides: 0 },
      { nom: 'Quinoa cuit', quantite: 150, unite: 'g', glucides: 28 },
      { nom: 'Champignons de Paris', quantite: 100, unite: 'g', glucides: 3 },
      { nom: 'Oignon', quantite: 60, unite: 'g', glucides: 6 },
      { nom: 'Ail', quantite: 10, unite: 'g', glucides: 2 },
      { nom: 'Huile d\'olive', quantite: 10, unite: 'ml', glucides: 0 },
      { nom: 'Sauce soja légère', quantite: 15, unite: 'ml', glucides: 3 },
    ],
    etapes: [
      'Cuire le quinoa selon les instructions (ratio 1:2 avec l\'eau, 15 min).',
      'Émincer l\'oignon et l\'ail, trancher les champignons.',
      'Faire chauffer l\'huile dans une poêle à feu vif.',
      'Saisir le bœuf 2-3 minutes, réserver.',
      'Faire revenir oignon, ail et champignons 5 minutes.',
      'Remettre le bœuf, ajouter la sauce soja, mélanger 2 minutes.',
      'Servir sur le quinoa chaud.',
    ],
    conseil: 'Le quinoa a un index glycémique modéré. Préférez des portions de 100-120g cuit pour limiter l\'impact glycémique.',
  },
  {
    id: 3,
    nom: 'Poulet rôti aux herbes et patate douce',
    categorie: 'volaille',
    image: 'https://readdy.ai/api/search-image?query=roasted%20chicken%20breast%20with%20herbs%20sweet%20potato%20wedges%20healthy%20balanced%20meal%20white%20plate%20bright%20food%20photography%20clean%20simple%20background&width=600&height=400&seq=plat3&orientation=landscape',
    resume: 'Blanc de poulet rôti aux herbes de Provence avec patate douce rôtie et haricots verts. Repas complet et savoureux.',
    nutriScore: 'A',
    glucidesTotaux: 35,
    calories: 410,
    ingredients: [
      { nom: 'Blanc de poulet', quantite: 180, unite: 'g', glucides: 0 },
      { nom: 'Patate douce', quantite: 150, unite: 'g', glucides: 28 },
      { nom: 'Haricots verts', quantite: 120, unite: 'g', glucides: 5 },
      { nom: 'Herbes de Provence', quantite: 5, unite: 'g', glucides: 1 },
      { nom: 'Huile d\'olive', quantite: 15, unite: 'ml', glucides: 0 },
      { nom: 'Ail en poudre', quantite: 3, unite: 'g', glucides: 1 },
    ],
    etapes: [
      'Préchauffer le four à 200°C.',
      'Badigeonner le poulet d\'huile d\'olive, herbes et ail en poudre.',
      'Couper la patate douce en quartiers, assaisonner.',
      'Enfourner poulet et patate douce 25-30 minutes.',
      'Cuire les haricots verts à la vapeur 8 minutes.',
      'Vérifier la cuisson du poulet (jus clair), servir.',
    ],
    conseil: 'La patate douce a un index glycémique plus bas que la pomme de terre classique. Idéale pour les diabétiques !',
  },
  {
    id: 4,
    nom: 'Buddha bowl végétarien aux lentilles',
    categorie: 'vegetarien',
    image: 'https://readdy.ai/api/search-image?query=colorful%20vegetarian%20buddha%20bowl%20with%20lentils%20chickpeas%20avocado%20cherry%20tomatoes%20spinach%20quinoa%20healthy%20vegan%20meal%20white%20bowl%20bright%20food%20photography&width=600&height=400&seq=plat4&orientation=landscape',
    resume: 'Bowl coloré avec lentilles corail, pois chiches, avocat, tomates cerises et épinards. Riche en fibres et protéines végétales.',
    nutriScore: 'A',
    glucidesTotaux: 52,
    calories: 520,
    ingredients: [
      { nom: 'Lentilles corail cuites', quantite: 120, unite: 'g', glucides: 20 },
      { nom: 'Pois chiches cuits', quantite: 80, unite: 'g', glucides: 14 },
      { nom: 'Avocat', quantite: 80, unite: 'g', glucides: 2 },
      { nom: 'Tomates cerises', quantite: 100, unite: 'g', glucides: 4 },
      { nom: 'Épinards frais', quantite: 60, unite: 'g', glucides: 1 },
      { nom: 'Quinoa cuit', quantite: 80, unite: 'g', glucides: 15 },
      { nom: 'Huile d\'olive', quantite: 10, unite: 'ml', glucides: 0 },
      { nom: 'Jus de citron', quantite: 15, unite: 'ml', glucides: 1 },
      { nom: 'Graines de sésame', quantite: 10, unite: 'g', glucides: 1 },
    ],
    etapes: [
      'Cuire les lentilles corail 15 minutes dans de l\'eau bouillante salée.',
      'Rincer et égoutter les pois chiches.',
      'Couper l\'avocat en tranches, les tomates cerises en deux.',
      'Cuire le quinoa selon les instructions.',
      'Disposer tous les ingrédients en sections dans un grand bol.',
      'Arroser d\'huile d\'olive et de jus de citron.',
      'Parsemer de graines de sésame et servir.',
    ],
    conseil: 'Les légumineuses sont excellentes pour les diabétiques : riches en fibres, elles ralentissent l\'absorption des glucides et stabilisent la glycémie.',
  },
];
